import {
  ParentComponent,
  JSX,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  useContext,
  Show,
} from "solid-js";
import {
  PROFILES,
  PROFILE_STORAGE_KEY,
  getProfileConfig,
} from "../config/profiles";
import { isValidProfileId } from "../config/profileRegistry";

type ProfileId = string | null;

interface ProfileContextValue {
  profile: () => ProfileId;
  profileConfig: () => ReturnType<typeof getProfileConfig>;
  setProfile: (id: ProfileId) => void;
  clearProfile: () => void;
}

const ProfileContext = createContext<ProfileContextValue>();

// Centralized validation (single source of truth for IDs).
// PROFILES remains the UI list; registry owns the canonical IDs.
const isValidProfile = (id: string | null | undefined): id is string =>
  !!id && isValidProfileId(id);

const cleanupQueryString = (params: URLSearchParams) => {
  params.delete("profile");
  const search = params.toString();
  const newUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", newUrl);
};

const resolveInitialProfile = (): ProfileId => {
  if (typeof window === "undefined") return null;

  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("profile");
    if (isValidProfile(fromQuery)) {
      cleanupQueryString(params);
      return fromQuery!;
    }
  } catch {
    // ignore: malformed URL or URLSearchParams not supported in this environment
  }

  try {
    const stored = window.localStorage.getItem(PROFILE_STORAGE_KEY);
    if (isValidProfile(stored)) {
      return stored;
    }
  } catch {
    // ignore: storage may be unavailable (privacy mode / blocked / disabled)
  }

  const envProfile = import.meta.env.VITE_DEFAULT_PROFILE as string | undefined;
  if (isValidProfile(envProfile)) {
    return envProfile!;
  }

  return null;
};

let activeProfile: ProfileId = null;

export const getActiveProfile = () => activeProfile;

export const ProfileProvider: ParentComponent = (props) => {
  const [profile, setProfileSignal] = createSignal<ProfileId>(resolveInitialProfile());

  const applyProfile = (value: ProfileId) => {
    activeProfile = value;
    setProfileSignal(value);
  };

  createEffect(() => {
    activeProfile = profile();
  });

  createEffect(() => {
    const current = profile();
    if (typeof window === "undefined") return;
    try {
      if (current) {
        window.localStorage.setItem(PROFILE_STORAGE_KEY, current);
      } else {
        window.localStorage.removeItem(PROFILE_STORAGE_KEY);
      }
    } catch {
      // ignore: storage may be unavailable (privacy mode / blocked / disabled)
    }
  });

  const profileConfig = createMemo(() => getProfileConfig(profile()));

  const value: ProfileContextValue = {
    profile,
    profileConfig,
    setProfile: (id) => applyProfile(id),
    clearProfile: () => applyProfile(null),
  };

  return <ProfileContext.Provider value={value}>{props.children}</ProfileContext.Provider>;
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};

export const RequireProfile: ParentComponent<{ fallback: JSX.Element }> = (
  props,
) => {
  const { profile } = useProfile();
  return (
    <Show when={profile()} fallback={props.fallback}>
      {props.children}
    </Show>
  );
};
